import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Route guard for any authenticated route.
 *
 * - Not signed in   → /auth
 * - Signed in, MFA pending (aal1 + has factor) → /auth/mfa  (cannot be skipped)
 * - Otherwise renders children.
 *
 * The MFA redirect is the bypass-prevention frontend layer. Sensitive backend
 * endpoints additionally check `aal === 'aal2'` on the JWT, so even if a user
 * navigated around this guard, server-side calls would still fail.
 */
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { user, loading, mfaRequired } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-[11px] uppercase tracking-[0.22em] text-primary tui-status">loading_</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace state={{ from: location }} />;
  if (mfaRequired && location.pathname !== "/auth/mfa") {
    return <Navigate to="/auth/mfa" replace />;
  }
  return <>{children}</>;
};

export default RequireAuth;
